import React, { useState, useContext } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { IconArrowLeft, IconBuildingBank, IconCreditCard, IconCirclePlus, IconCircleCheck } from '@tabler/icons-react';
import { useNavigate } from 'react-router';

import styled from 'styled-components';

import { AsideBody, AsideHeader, AsideSubTitle, AsideDescription } from 'pages/Totem/Aside/AsideComponents';
import { getStripeApiKey } from 'pages/Settings/SettingsPage/SettingsPayment/utils';
import { CreditCardForm } from 'pages/Totem/Aside/Billing/CreditCardForm';
import { IBANForm } from 'pages/Totem/Aside/Billing/IBANForm';
import { BillingInfoForm } from 'pages/Totem/Aside/Billing/BillingInfoForm';

import { BillingInfoUnavailable } from 'components/BillingInfoUnavailable';
import { Loading } from 'components/Loading';

import { UserContext } from 'constants/user-context';
import { STRIPE_CARD, STRIPE_SEPA_DEBIT, requiresStripePaymentMethod } from 'constants/organization';

import { IconButton, TotemButton } from 'ui/Button';

import { colors } from 'styles/theme';

import { capitalize } from 'utils';
import { useOrganizationBillingInfo } from 'data/hooks/useOrganizationBillingInfo';
import { OrganizationPaymentType, TotemFragmentFragment as Totem } from 'data/__generated__';

interface BillingContainerProps {
    currentTotem: Totem | undefined;
    setIsBillingAsideOpen: (open: boolean) => void;
}

export const BillingContainer: React.FC<BillingContainerProps> = ({ currentTotem, setIsBillingAsideOpen }) => {
    const ICON_SIZE = '18px';
    const navigate = useNavigate();
    const [selectedPaymentType, setselectedPaymentType] = useState<string | undefined>(undefined);
    const { organization, user } = useContext(UserContext);
    // Infos de facturation = Odoo, chargées à part de l'organisation : une panne Odoo n'affiche une erreur
    // QUE dans cette section, sans empêcher le panier ni le reste de l'app de fonctionner.
    const { billingInfo, isBillingInfoLoading, billingInfoError, refetchBillingInfo } = useOrganizationBillingInfo();
    const cards = organization?.cards ?? [];
    const paymentType = organization?.paymentType ?? undefined;
    const needsPaymentMethod = requiresStripePaymentMethod(paymentType);
    const defaultSourceObject = cards?.find((card: { id: string }) => card?.id === billingInfo?.defaultPaymentMethod);
    // const [stripePromise] = useState(() => loadStripe(getStripeApiKey(organization)));
    const [stripePromise] = useState(() => (organization ? loadStripe(getStripeApiKey(organization)) : null));
    const isCreditCardActive = selectedPaymentType === 'creditCard';
    const isIBANActive = selectedPaymentType === 'IBAN';
    const billingRequiredInfo = [
        'invoiceEmail',
        'invoiceName',
        'invoiceAddress',
        'invoiceCity',
        'invoiceZipcode',
        'invoiceCountry',
    ];

    const hasBillingInfo =
        !!billingInfo && billingRequiredInfo.every((prop) => !!(billingInfo as Record<string, unknown>)[prop]);
    // `null` = l'utilisateur n'a pas encore choisi : on dérive alors de la donnée, qui arrive de façon
    // asynchrone (un state initialisé au premier rendu serait figé sur `billingInfo` encore vide).
    const [isModifyingBillingInfo, setIsModifyingBillingInfo] = useState<boolean | null>(null);
    const isEditingBillingInfo = isModifyingBillingInfo ?? !hasBillingInfo;

    // Sans organisation il n'y a rien à facturer, et les formulaires Stripe en exigent une. (Auparavant
    // implicite : TypeScript déduisait `organization` non nul du test sur `organization.billingInfo`.)
    if (!organization) {
        return null;
    }

    return (
        <>
            <AsideHeader>
                <IconButton data-test="aside-return-button" $white $large onClick={() => setIsBillingAsideOpen(false)}>
                    <IconArrowLeft size={25} />
                </IconButton>
            </AsideHeader>
            <AsideBody>
                <AsideSubTitle>
                    1 - Informations de facturation{' '}
                    {hasBillingInfo && <IconCircleCheck size="22px" color={colors.green} />}
                </AsideSubTitle>
                {billingInfoError ? (
                    <BillingInfoContainer>
                        <BillingInfoUnavailable onRetry={refetchBillingInfo} />
                    </BillingInfoContainer>
                ) : isBillingInfoLoading ? (
                    <Loading relative />
                ) : (
                    <>
                        {isEditingBillingInfo ? (
                            <BillingInfoContainer>
                                <BillingInfoForm
                                    currentTotem={currentTotem}
                                    currentUser={user}
                                    organization={organization}
                                    billingInfo={billingInfo}
                                    setIsModifyingBillingInfo={setIsModifyingBillingInfo}
                                />
                            </BillingInfoContainer>
                        ) : (
                            <TotemButton
                                $type="secondary"
                                $margins={[0, 2]}
                                data-test="modify-billing-info"
                                $fontSize="1em"
                                onClick={() => setIsModifyingBillingInfo(true)}
                            >
                                Modifier les informations de facturation
                            </TotemButton>
                        )}
                        <AsideSubTitle>
                            2 - Moyen de paiement{' '}
                            {(!needsPaymentMethod || (billingInfo?.defaultPaymentMethod && defaultSourceObject)) && (
                                <IconCircleCheck size="22px" color={colors.green} />
                            )}
                        </AsideSubTitle>
                        {/* Totem ne prélève pas cette organisation (Odoo encaisse, ou personne) : on annonce
                            son mode d'encaissement et on n'affiche AUCUN formulaire Stripe. */}
                        {!needsPaymentMethod ? (
                            <PaymentTypeToggle>
                                <PaymentOption as="div" $clickable={false}>
                                    <SelectLine>
                                        <NameAndIcon>
                                            {paymentType === OrganizationPaymentType.Odoo ? (
                                                <>
                                                    <IconBuildingBank size={ICON_SIZE} />
                                                    Virement bancaire
                                                </>
                                            ) : (
                                                'Aucun paiement requis'
                                            )}
                                        </NameAndIcon>
                                    </SelectLine>
                                </PaymentOption>
                            </PaymentTypeToggle>
                        ) : !hasBillingInfo ? (
                            <AsideDescription>
                                Veuillez ajouter vos informations de facturation avant d'ajouter un moyen de paiement.
                            </AsideDescription>
                        ) : billingInfo?.defaultPaymentMethod && defaultSourceObject ? (
                            <>
                                <PaymentTypeToggle>
                                    <PaymentOption as="div" $clickable={false}>
                                        <SelectLine>
                                            <NameAndIcon>
                                                {defaultSourceObject?.type === STRIPE_SEPA_DEBIT ? (
                                                    <IconBuildingBank size={ICON_SIZE} />
                                                ) : (
                                                    <IconCreditCard size={ICON_SIZE} />
                                                )}
                                                {defaultSourceObject?.type === STRIPE_SEPA_DEBIT
                                                    ? 'SEPA (Virement bancaire)'
                                                    : defaultSourceObject?.type === STRIPE_CARD &&
                                                      `${capitalize(
                                                          defaultSourceObject?.card?.brand ||
                                                              defaultSourceObject?.brand ||
                                                              '',
                                                      )}`}
                                            </NameAndIcon>
                                            {defaultSourceObject?.type === STRIPE_CARD &&
                                                `• • • • • • • • • • • • ${
                                                    defaultSourceObject?.card?.last4 || defaultSourceObject?.last4
                                                }`}
                                        </SelectLine>
                                    </PaymentOption>
                                </PaymentTypeToggle>
                                <TotemButton
                                    $type="secondary"
                                    $margins={[0, 2]}
                                    $fontSize="1em"
                                    onClick={() => navigate('/settings/payment', { relative: 'path' })}
                                >
                                    Modifier le moyen de paiement utilisé
                                </TotemButton>
                            </>
                        ) : (
                            <Elements stripe={stripePromise}>
                                <PaymentTypeToggle>
                                    <PaymentOption
                                        $clickable={!isCreditCardActive}
                                        data-test="payment-methods-credit-card-button"
                                        onClick={() => setselectedPaymentType('creditCard')}
                                    >
                                        <SelectLine>
                                            <NameAndIcon>
                                                <IconCreditCard size={ICON_SIZE} />
                                                Carte bancaire
                                            </NameAndIcon>
                                            {selectedPaymentType !== 'creditCard' && (
                                                <IconCirclePlus size={ICON_SIZE} />
                                            )}
                                        </SelectLine>
                                        {selectedPaymentType === 'creditCard' && (
                                            <CreditCardForm
                                                organization={organization}
                                                currentUser={user}
                                                onSubmitCompletion={() => setIsBillingAsideOpen(false)}
                                            />
                                        )}
                                    </PaymentOption>
                                    <PaymentOption
                                        $clickable={!isIBANActive}
                                        data-test="payment-methods-iban-button"
                                        onClick={() => setselectedPaymentType('IBAN')}
                                    >
                                        <SelectLine>
                                            <NameAndIcon>
                                                <IconBuildingBank size={ICON_SIZE} />
                                                IBAN
                                            </NameAndIcon>
                                            {selectedPaymentType !== 'IBAN' && <IconCirclePlus size={ICON_SIZE} />}
                                        </SelectLine>
                                        {selectedPaymentType === 'IBAN' && (
                                            <IBANForm
                                                organization={organization}
                                                currentUser={user}
                                                onSubmitCompletion={() => setIsBillingAsideOpen(false)}
                                            />
                                        )}
                                    </PaymentOption>
                                </PaymentTypeToggle>
                            </Elements>
                        )}
                    </>
                )}
            </AsideBody>
        </>
    );
};

const BillingInfoContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    margin: 0 2em;
`;

const PaymentTypeToggle = styled.div`
    padding: 0 2em;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
`;

const SelectLine = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

const NameAndIcon = styled.div`
    display: flex;
    align-items: center;
    font-size: 18px;
    svg {
        margin-right: 5px;
    }
`;
type PaymentOptionProps = {
    $clickable?: boolean;
};
const PaymentOption = styled.div<PaymentOptionProps>`
    background-color: white;
    border-radius: ${({ theme }) => theme.variables.borderRadius};
    width: 100%;
    padding: 1em;
    cursor: ${({ $clickable }) => $clickable && 'pointer'};
    margin-bottom: 1em;
`;
